import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient";

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 460,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
  padding: "2rem",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: "0.45rem",
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#0f172a",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.9rem 1rem",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: "0.95rem",
  outline: "none",
  boxSizing: "border-box",
};

const errorTextStyle: React.CSSProperties = {
  color: "#b91c1c",
  fontSize: "0.82rem",
  marginTop: "0.45rem",
};

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    password?: string;
    passwordConfirm?: string;
  }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const establishRecoverySession = async () => {
      try {
        const hash = window.location.hash.startsWith("#")
          ? new URLSearchParams(window.location.hash.slice(1))
          : null;

        const accessToken = hash?.get("access_token");
        const refreshToken = hash?.get("refresh_token");
        const type = hash?.get("type");

        if (type === "recovery" && accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            if (mounted) {
              setFormError("This password reset link is invalid or has expired.");
              setSessionReady(false);
            }
            return;
          }
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (mounted) {
          if (session) {
            setSessionReady(true);
          } else {
            setFormError("This password reset link is invalid or has expired.");
            setSessionReady(false);
          }
        }
      } catch (err) {
        console.error("Reset session error", err);
        if (mounted) {
          setFormError("Unable to verify your password reset session.");
          setSessionReady(false);
        }
      }
    };

    establishRecoverySession();

    return () => {
      mounted = false;
    };
  }, []);

  const validate = () => {
    const errors: { password?: string; passwordConfirm?: string } = {};

    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }

    if (!passwordConfirm) {
      errors.passwordConfirm = "Please confirm your new password.";
    } else if (passwordConfirm !== password) {
      errors.passwordConfirm = "Passwords do not match.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormMessage(null);

    if (!validate()) return;
    if (!sessionReady) {
      setFormError("This password reset session is not available.");
      return;
    }

    try {
      setSubmitting(true);

      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        setFormError(error.message);
        return;
      }

      setFormMessage("Your password has been updated. Redirecting to login...");

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1400);
    } catch (err) {
      console.error("Reset password error", err);
      setFormError("Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      style={{
        minHeight: "calc(100vh - 160px)",
        background:
          "linear-gradient(180deg, #f8fafc 0%, #ffffff 40%, #f8fafc 100%)",
        padding: "3rem 1.25rem 4rem",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "2rem",
          alignItems: "center",
        }}
      >
        <section>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.45rem 0.8rem",
              borderRadius: 999,
              background: "#e2e8f0",
              color: "#334155",
              fontSize: "0.82rem",
              fontWeight: 700,
              letterSpacing: "0.01em",
              marginBottom: "1rem",
            }}
          >
            HnB secure reset
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#0f172a",
            }}
          >
            Create a new password
          </h1>

          <p
            style={{
              marginTop: "1rem",
              maxWidth: 560,
              fontSize: "1.02rem",
              lineHeight: 1.75,
              color: "#475569",
            }}
          >
            Choose a new password for your customer account to restore access to
            bookings, previews, and saved account activity.
          </p>

          <div
            style={{
              marginTop: "1.5rem",
              display: "grid",
              gap: "0.9rem",
              color: "#334155",
              fontSize: "0.95rem",
            }}
          >
            <div>• Use at least 6 characters.</div>
            <div>• Confirm the same password twice before submitting.</div>
            <div>• Password reset remains part of the separate customer auth flow.</div>
          </div>
        </section>

        <section>
          <div style={cardStyle}>
            <h2
              style={{
                margin: 0,
                fontSize: "1.5rem",
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              Set new password
            </h2>

            <p
              style={{
                marginTop: "0.6rem",
                marginBottom: "1.5rem",
                color: "#64748b",
                lineHeight: 1.6,
                fontSize: "0.95rem",
              }}
            >
              Enter your new password below to complete the reset.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ marginBottom: "1rem" }}>
                <label htmlFor="password" style={labelStyle}>
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (formError) setFormError(null);
                    if (formMessage) setFormMessage(null);
                    if (fieldErrors.password) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        password: undefined,
                      }));
                    }
                  }}
                  style={inputStyle}
                  aria-invalid={fieldErrors.password ? "true" : "false"}
                />
                {fieldErrors.password && (
                  <p style={errorTextStyle}>{fieldErrors.password}</p>
                )}
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label htmlFor="passwordConfirm" style={labelStyle}>
                  Confirm new password
                </label>
                <input
                  id="passwordConfirm"
                  type="password"
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={(e) => {
                    setPasswordConfirm(e.target.value);
                    if (formError) setFormError(null);
                    if (formMessage) setFormMessage(null);
                    if (fieldErrors.passwordConfirm) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        passwordConfirm: undefined,
                      }));
                    }
                  }}
                  style={inputStyle}
                  aria-invalid={fieldErrors.passwordConfirm ? "true" : "false"}
                />
                {fieldErrors.passwordConfirm && (
                  <p style={errorTextStyle}>{fieldErrors.passwordConfirm}</p>
                )}
              </div>

              {formError && (
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "0.85rem 1rem",
                    borderRadius: 12,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    fontSize: "0.92rem",
                    lineHeight: 1.6,
                  }}
                >
                  {formError}
                </div>
              )}

              {formMessage && (
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "0.85rem 1rem",
                    borderRadius: 12,
                    background: "#ecfdf5",
                    border: "1px solid #a7f3d0",
                    color: "#065f46",
                    fontSize: "0.92rem",
                    lineHeight: 1.6,
                  }}
                >
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !sessionReady}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 999,
                  padding: "0.95rem 1rem",
                  background: submitting || !sessionReady ? "#475569" : "#111827",
                  color: "#ffffff",
                  fontSize: "0.96rem",
                  fontWeight: 700,
                  cursor: submitting || !sessionReady ? "not-allowed" : "pointer",
                  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14)",
                }}
              >
                {submitting ? "Updating password..." : "Update password"}
              </button>
            </form>

            <p
              style={{
                marginTop: "1rem",
                marginBottom: 0,
                fontSize: "0.92rem",
                color: "#64748b",
                lineHeight: 1.6,
              }}
            >
              Return to{" "}
              <Link
                to="/login"
                style={{
                  color: "#0f172a",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Login
              </Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ResetPasswordPage;